import PartServiceProvider from '../../../../src/services/PartServiceProvider'
import {
  AvailabilityError,
  AvailabilityErrorType,
  FieldUpdate,
  ProductLocationModel,
  ProductModel,
} from 'interfaces'
import {
  PartAvailabilityRequest,
  PartInfoRequest,
  PartInfoResponse,
  Prices,
} from '../../../../src/store/cart/ValidationInterfaces'
import { buildUpdate, hasPriceChanged } from './Utils_testable'
import {
  ICartErrorProduct,
  IVehiclePriceChangedParts,
  SellerCartVehicle,
} from '../store/ISellerCart'

export const buildAvailabilityRequest = (
  product: ProductModel,
  quantityRequested: number,
  location?: ProductLocationModel
): PartInfoRequest => {
  return {
    lineNo: 0,
    lineCode: product.lineCode,
    manufacturerName: product.manufacturerName || '',
    partNumber: product.partNumber,
    brand: product.brand || '',
    description: product.description || '',
    quantityRequested,
    unitOfMeasure: location?.unitOfMeasure,
    unitCorePrice: location?.coreCost,
    unitCostPrice: location?.cost,
    isPriceOverride: true,
    priceOverrideMsg: '',
    locationId: location?.locationId,
    locationDescription: location?.called,
  }
}

export const fetchProduct = (
  partsInfo: PartAvailabilityRequest,
  coverageKey?: string
): Promise<{ part: PartInfoResponse; hasTimeout: boolean }> => {
  return PartServiceProvider.getAvailability(partsInfo, coverageKey).then(
    (res) => ({
      part: res.parts[0],
      hasTimeout: res.snwPrimaryStatus === 'TIMEOUT',
    })
  )
}

// validating prices with price breaks
export const productCostWithPriceBreaks = (
  activeLocation: ProductLocationModel,
  currentQty: number
): number => {
  const priceBreaks = activeLocation?.priceBreaks || []
  if (priceBreaks.length > 0) {
    const lastPriceBreak = priceBreaks[priceBreaks.length - 1]
    for (const priceBreak of priceBreaks || []) {
      if (
        priceBreak?.minQty <= currentQty &&
        priceBreak?.maxQty >= currentQty
      ) {
        return priceBreak?.unitCost
      }
      // checking minQty for lastPriceBreak, as maxQty returns 0 for some parts
      if (lastPriceBreak?.minQty && currentQty >= lastPriceBreak?.minQty) {
        return lastPriceBreak?.unitCost
      }
    }
  }
  return activeLocation ? activeLocation.cost : 0
}

const calculatePriceUpdates = (
  location: ProductLocationModel | undefined,
  updatedLocation: ProductLocationModel,
  quantityRequested: number
): Array<FieldUpdate> => {
  const updates: Array<FieldUpdate> = []

  const oldCost: string =
    (location?.priceBreaks?.length > 0
      ? productCostWithPriceBreaks(location, quantityRequested)?.toString()
      : location?.cost?.toString()) || '0'
  const newCost: string =
    (updatedLocation?.priceBreaks?.length > 0
      ? productCostWithPriceBreaks(
          updatedLocation,
          quantityRequested
        )?.toString()
      : updatedLocation?.cost?.toString()) || '0'

  if (hasPriceChanged(oldCost, newCost))
    updates.push(buildUpdate(Prices.COST, oldCost, newCost))

  return updates
}

export const validatePricesWithPriceBreaks = (
  oldLocation: ProductLocationModel | undefined,
  newLocation: ProductLocationModel,
  quantityRequested: number
): Array<AvailabilityError> => {
  const availabilityErrors: Array<AvailabilityError> = []
  const priceUpdates = calculatePriceUpdates(
    oldLocation,
    newLocation,
    quantityRequested
  )

  if (priceUpdates.length > 0)
    availabilityErrors.push({
      errorType: AvailabilityErrorType.PRICE_OVERRIDE,
      fieldUpdates: priceUpdates,
      location: newLocation,
    })

  return availabilityErrors
}

export const getVehicleWithPriceChangedParts = (
  data: SellerCartVehicle[]
): IVehiclePriceChangedParts[] => {
  return data.map((v) => {
    // Extract the vehicle and any parts that have price override errors
    const priceChangedParts: ICartErrorProduct[] =
      v.sellers.flatMap(
        (s) =>
          s.locations?.flatMap(
            (l) =>
              l.parts
                ?.filter((p) =>
                  p.errors?.some(
                    (e) => e.errorType === AvailabilityErrorType.PRICE_OVERRIDE
                  )
                )
                ?.map((p) => ({
                  seller: s,
                  location: l,
                  product: p,
                })) || []
          ) || []
      ) || []

    return {
      vehicle: v.vehicle,
      parts: priceChangedParts,
    }
  })
}
